// ABOUTME: Pure attribute/text matcher — generalized from the legacy per-brand highlighter (App.js).
// ABOUTME: No React, no DOM: scans live speech text for mentions of a product's own catalogue fields only.

// Generic English stopwords excluded from value-word matching (too short/common to be a meaningful
// signal on their own). 'hour'/'hours' is a compromise: it is a duration-unit assumption tied to
// how the legacy per-brand highlighter (App.js) already filtered its value words — kept for parity
// with that shared list rather than as a genuinely universal stopword; a future tenant with an
// hours-based spec value could see it under-match. Not otherwise domain vocabulary.
const GENERIC_STOPWORDS = new Set([
  'with', 'from', 'this', 'that', 'have', 'custom', 'type', 'made', 'the', 'and', 'for', 'hour', 'hours',
]);

const humanizeKey = (key) => String(key).replace(/_/g, ' ');
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const keyMatches = (lowerText, key) => {
  const cleanKey = escapeRegExp(humanizeKey(key).toLowerCase());
  return cleanKey.length > 2 && new RegExp(`\\b${cleanKey}\\b`, 'i').test(lowerText);
};

// A value word is a match candidate on one of two grounds:
//  • an ordinary word longer than 3 chars that is not generic filler, or
//  • a digit-bearing token of 2+ chars ("86", "40mm", "18k", "316l"). These are exactly the tokens
//    the length rule was silently discarding, and they are often the ONLY distinctive part of a
//    numeric spec — a value like "86 Hours" has no other matchable word ("hours" is filler), so
//    without this clause that attribute could never match by value at all.
const wordQualifies = (word) => (
  (/\d/.test(word) && word.length >= 2)
  || (word.length > 3 && !GENERIC_STOPWORDS.has(word))
);

const valueMatches = (lowerText, value) => {
  // '.' survives the strip so a decimal stays ONE token ("11.45mm"), then sentence punctuation is
  // trimmed off the token edges. Without this, "11.45mm" shattered into a bare "11" + "45mm" and
  // the "11" matched any unrelated eleven in the narration ("the 11 o'clock index").
  const words = String(value).toLowerCase().replace(/[^a-z0-9.\s-]/g, ' ').split(/\s+/)
    .map((word) => word.replace(/^\.+|\.+$/g, ''));
  return words.some(
    (word) => wordQualifies(word) && new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i').test(lowerText)
  );
};

/**
 * Merges a product's `specifications` and `technical_specs` into one flat map. `specifications`
 * wins on a key present in both — shared by the matcher and `resolveTagContent.js` so a tag is
 * never triggered by one value and rendered with a different one.
 */
export function mergeSpecs(product) {
  return {
    ...(product?.technical_specs || {}),
    ...(product?.specifications || {}),
  };
}

/**
 * Scans `text` (live agent speech) for mentions of `product`'s own `specifications` /
 * `technical_specs` keys or values. Two match strategies only — direct key mention, direct value
 * mention — no hardcoded category word-lists (AC-10): a tenant's catalogue is the only vocabulary
 * source, so this works identically for any brand's product shape.
 *
 * @param {object} params
 * @param {string} params.text - live agent transcript text (any case).
 * @param {object|null} params.product - resolved product envelope with `specifications` and/or
 *   `technical_specs` objects.
 * @param {string} [params.chapterId] - active reel chapter id, used to filter by `tagEligibility`.
 * @param {object} [params.tagEligibility] - D3 map of `{ [attributeKey]: [chapterId, ...] }`. When
 *   omitted OR empty (the wire shape a tenant with no reel eligibility config sends), every key is
 *   eligible — non-reel callers (e.g. the legacy 2D storefront highlighter) have no chapter concept.
 * @param {Iterable<string>} [params.exclude] - keys already matched (e.g. this speaking turn);
 *   skipped so a match isn't reported twice while the source text keeps growing.
 * @returns {string[]} newly matched attribute keys, in catalogue order.
 */
export function matchAttributes({ text, product, chapterId, tagEligibility, exclude }) {
  if (!product || !text) return [];
  const lowerText = text.toLowerCase();
  const excludeSet = exclude instanceof Set ? exclude : new Set(exclude || []);
  const eligibility = tagEligibility && Object.keys(tagEligibility).length ? tagEligibility : null;

  const allSpecs = mergeSpecs(product);

  const matched = [];
  for (const [key, value] of Object.entries(allSpecs)) {
    if (excludeSet.has(key)) continue;
    if (eligibility && !(eligibility[key] || []).includes(chapterId)) continue;
    if (keyMatches(lowerText, key) || valueMatches(lowerText, value)) matched.push(key);
  }
  return matched;
}
